import { type ChangeEvent, type ReactNode, useCallback } from 'react'
import cx from 'classnames'
import styles from './FilterControls.module.css'
import {
  Dropdown,
  DropdownItem,
  DropdownItemDescription,
  DropdownItemTitle,
  DropdownScroll,
  DropdownTitle,
  useDropdownDensity,
} from '@/components/Dropdown'
import { Icon, type IconName } from '@/components/Icon'
import { Button, RawButton } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { Tooltip } from '@/components/Tooltip'

interface FilterControlsProps {
  active: boolean
  /**
   * The applied filters, as `AppliedFilterChip`s.
   */
  appliedFilters: ReactNode
  /**
   * Content of the "Add Filter" dropdown, usually `AvailableFilters`.
   */
  availableFilters: ReactNode
  onClearAll: () => void
  testId?: string
}

/**
 * The applied filters as chips, with buttons to add filters and clear them.
 */
export function FilterControls({
  active,
  appliedFilters,
  availableFilters,
  onClearAll,
  testId,
}: FilterControlsProps) {
  return (
    <div
      className={cx(
        styles.filterControls,
        active && styles.filterControlsActive,
      )}
    >
      <div className={cx(styles.selectedFiltersRow)}>
        <div className={cx(styles.selectedFilterList)}>{appliedFilters}</div>
        <Dropdown
          autoDismiss
          position="bottom-start"
          button={
            <Button
              label="+ Add Filter"
              variant="text"
              testId={testId && `${testId}-add-filter-button`}
            />
          }
        >
          <DropdownScroll>{availableFilters}</DropdownScroll>
        </Dropdown>
      </div>
      <Button
        label="Clear All"
        variant="text"
        testId={testId && `${testId}-clear-all-button`}
        onPress={onClearAll}
      />
    </div>
  )
}

interface AppliedFilterChipProps {
  label: string
  isEditing: boolean
  onStartEditing: () => void
  onStopEditing: () => void
  onRemove: () => void
  /**
   * The filter's value editor, shown while editing.
   */
  children: ReactNode
}

/**
 * An applied filter, which opens its editor when clicked.
 */
export function AppliedFilterChip({
  label,
  isEditing,
  onStartEditing,
  onStopEditing,
  onRemove,
  children,
}: AppliedFilterChipProps) {
  return (
    <Dropdown
      isOpen={isEditing}
      contentOverButton
      allowOverflow
      onRequestClose={onStopEditing}
      position="bottom-start"
      button={
        <div className={styles.selectedFilter}>
          <Chip variant="filter" onPress={onStartEditing} onRemove={onRemove}>
            {label}
          </Chip>
        </div>
      }
    >
      {children}
    </Dropdown>
  )
}

interface FilterViewHeaderProps {
  title: string
  onClear: () => void
  onSelectAll?: () => void
  testId?: string
}

/**
 * The title row of a filter editor, with its clear action.
 */
export function FilterViewHeader({
  title,
  testId,
  onClear,
  onSelectAll,
}: FilterViewHeaderProps) {
  const isCompact = useDropdownDensity() === 'compact'
  if (isCompact) {
    return (
      <div className={styles.compactTitleRow}>
        <span className={styles.compactTitle}>{title}</span>
        {onSelectAll && (
          <RawButton className={styles.compactAction} onPress={onSelectAll}>
            Select all
          </RawButton>
        )}
        <RawButton
          className={styles.compactAction}
          onPress={onClear}
          testId={testId}
        >
          Clear
        </RawButton>
      </div>
    )
  }

  return (
    <div className={cx(styles.filterTitleRow)}>
      <DropdownTitle>{title}</DropdownTitle>
      {onSelectAll && (
        <div className={cx(styles.action)}>
          <Button label="Select all" variant="text" onPress={onSelectAll} />
        </div>
      )}
      <div className={cx(styles.action)}>
        <Button
          label="Clear"
          variant="text"
          testId={testId}
          onPress={onClear}
        />
      </div>
    </div>
  )
}

interface FilterViewItemGroupProps {
  children: ReactNode
}

/**
 * Groups the fields of a filter editor.
 */
export function FilterViewItemGroup({ children }: FilterViewItemGroupProps) {
  return <div className={styles.filterItemContainer}>{children}</div>
}

interface FilterViewItemProps {
  title: string
  children: ReactNode
}

/**
 * A titled field of a filter editor.
 */
export function FilterViewItem({ title, children }: FilterViewItemProps) {
  return (
    <div className={cx(styles.filterItem)}>
      <div className={cx(styles.filterItemText)}>{title}</div>
      {children}
    </div>
  )
}

/**
 * Separates sections of a filter editor.
 */
export function FilterViewDivider() {
  return <div className={styles.divider} />
}

interface AvailableFiltersProps {
  /**
   * The filters to choose from, as `AvailableFilterItem`s.
   */
  children: ReactNode
}

/**
 * The list of filters that can be added.
 */
export function AvailableFilters({ children }: AvailableFiltersProps) {
  return (
    <>
      <DropdownTitle>Add filter</DropdownTitle>
      {children}
    </>
  )
}

interface AvailableFilterItemProps {
  icon: IconName
  title: string
  /**
   * The current value, when the filter is already applied.
   */
  description?: string
  /**
   * The title of an applied filter this one cannot be combined with.
   */
  conflictTitle?: string
  onPress: () => void
  testId?: string
}

/**
 * A filter that can be added.
 */
export function AvailableFilterItem({
  icon,
  title,
  description,
  conflictTitle,
  onPress,
  testId,
}: AvailableFilterItemProps) {
  const item = (
    <DropdownItem
      icon={<Icon name={icon} color="var(--grey600)" />}
      iconRight={
        conflictTitle && <Icon name="info" size={18} color="var(--grey600)" />
      }
      isDisabled={conflictTitle !== undefined}
      onPress={onPress}
      testId={testId}
    >
      <DropdownItemTitle>{title}</DropdownItemTitle>
      {description !== undefined && (
        <DropdownItemDescription>{description}</DropdownItemDescription>
      )}
    </DropdownItem>
  )

  if (conflictTitle === undefined) {
    return item
  }

  return (
    <Tooltip
      content={`Conflicts with the ${conflictTitle} filter`}
      placement="left"
    >
      {item}
    </Tooltip>
  )
}

interface FilterSearchBarProps {
  onChange: (val: string) => void
}

/**
 * Searches the options of a filter editor.
 */
export function FilterSearchBar({ onChange }: FilterSearchBarProps) {
  const isCompact = useDropdownDensity() === 'compact'
  const change = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value)
    },
    [onChange],
  )

  if (isCompact) {
    return (
      <div className={styles.compactSearch}>
        <Icon name="search" size={13} />
        <input
          type="text"
          placeholder="Search"
          aria-label="Search options"
          className={styles.compactSearchInput}
          onChange={change}
        />
      </div>
    )
  }

  return (
    <div className={cx(styles.subFilterContent)}>
      <div className={styles.filterSearch}>
        <input
          type="text"
          placeholder="Search"
          className={styles.searchInput}
          onChange={change}
        />
        <Icon name="search" />
      </div>
    </div>
  )
}
